import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild, forwardRef, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  BehaviorSubject,
  Subject,
  debounceTime,
  distinctUntilChanged,
  from,
  takeUntil,
} from 'rxjs';
import {
  OrganizationByExactNameService,
  OrganizationByIdService,
  OrganizationSelectorService,
} from './organization-selector-input.service';
import {
  ControlValueAccessor,
  FormBuilder,
  FormControl,
  FormGroup,
  FormsModule,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
} from '@angular/forms';
import { MatSelect, MatSelectChange, MatSelectModule } from '@angular/material/select';
import {
  Organization,
  OrganizationResponse,
  PageInfo,
} from './graphql/get-organization-information-list.query';
import { coerceBooleanProperty } from '@angular/cdk/coercion';
import { MatPaginator, MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MatSort, Sort } from '@angular/material/sort';
import { MatBadgeModule } from '@angular/material/badge';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatInputModule } from '@angular/material/input';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatIconModule } from '@angular/material/icon';
import { MatTableDataSource } from '@angular/material/table';
import { AuthService } from 'app/core/auth/auth.service';
import { ResultQuery } from '@helpers/services/utils';
import {
  OrganizationInUser,
  UserInformationResponse,
} from 'app/core/user/graphql/get-user-id-and-id-organization.query';
import { UserService } from 'app/core/user/user.service';
import Keycloak, { KeycloakProfile } from 'keycloak-js';
import { MatDividerModule } from '@angular/material/divider';

type OrganizationSelectorChangeFn = (val: Organization | string | null) => void;

@Component({
  selector: 'organization-selector-input',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatPaginatorModule,
    MatSelectModule,
    MatBadgeModule,
    MatPaginatorModule,
    MatProgressBarModule,
    MatInputModule,
    MatTooltipModule,
    MatIconModule,
    MatDividerModule,
  ],
  templateUrl: './organization-selector-input.component.html',
  styleUrls: ['./organization-selector-input.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => OrganizationSelectorInputComponent),
      multi: true,
    },
    OrganizationByIdService,
    OrganizationSelectorService,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OrganizationSelectorInputComponent
  implements OnInit, ControlValueAccessor, OnDestroy, AfterViewInit, OnChanges {
  private readonly _dropDownDynamicService = inject(OrganizationSelectorService, { self: true });
  private readonly _dropDownOrgByIdDynamicService = inject(OrganizationByIdService, { self: true });
  private readonly _formBuilder = inject(FormBuilder);

  @Output() selectionChange = new EventEmitter<MatSelectChange>();
  /** Reference to the search input field */
  @ViewChild('searchSelectInput', { read: ElementRef })
  searchSelectInput!: ElementRef;
  @ViewChild('singleSelect') singleSelect!: MatSelect;
  @ViewChild(MatSelect) matSelect!: MatSelect;
  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;
  @Input() labelInput = '';
  @Input() placeHolderInput!: string;
  @Input() isDisabled = false; //poc
  @Input() isPrivateRecord?: boolean = false; //poc
  @Input() messagePrivateRecord?: string = ''; //poc
  @Input() destroyedSubject!: Subject<void>;

  selectListControl = new FormControl<Organization | null>(null);

  pageSizeOptions: number[] = [10, 15, 30];
  dataSearched = true;
  pageInfo!: PageInfo;
  pageIndex = 0; // Index of the current page
  pageSize = 5; // Page size (number of items per page)
  // Data source for MatTable, initialized later
  dataSource!: MatTableDataSource<Organization>;

  isLoadingDropdown$ = this._dropDownDynamicService.loading;
  searchForm!: FormGroup;

  dataItemList: Organization[] = [];
  private readonly _unsubscribeAll = new Subject<void>();
  private _required = false;
  private _previouslySelectedOrganization: Organization | null = null;
  private readonly _authService = inject(AuthService);
  private readonly _organizationByExactNameService = inject(OrganizationByExactNameService);
  private readonly _keycloak = inject(Keycloak);
  private readonly _checkRef = inject(ChangeDetectorRef);
  loadUserProfileObservable$ = from(this._keycloak.loadUserProfile());
  private readonly _userService = inject(UserService);

  organizations!: UserInformationResponse;
  organizationBehaviorSubject = new BehaviorSubject<OrganizationInUser | null>(null);
  organizationBehaviorSubject$ = this.organizationBehaviorSubject.asObservable();
  loadingUser = this._userService.loading;
  private readonly roles: string[] = [];
  selectedOrganizationName = '';
  private _searchSubInitialized = false;
  currentItem: Organization | null = null;

  constructor() {
    this.searchForm = this._formBuilder.group({
      searchBy: [''],
    });
  }

  @Input()
  get required(): boolean {
    return this._required;
  }

  set required(value: boolean) {
    this._required = coerceBooleanProperty(value);
  }

  ngOnInit(): void {
    const userRoles = this._authService.getUserRoles();
    this.roles.push(...userRoles);
    this.initialData();
    this.getUserInformation();
    this.loadOrganizations('');
    // Subscribe to page change event
    this._dropDownDynamicService.pageChanged$
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe((pageInfo: PageInfo) => {
        this.pageInfo = pageInfo;
      });
  }

  ngAfterViewInit(): void {
    // when the select dropdown panel is opened or closed
    this.singleSelect.openedChange
      .pipe(takeUntil(this.destroyedSubject ? this.destroyedSubject : this._unsubscribeAll))
      .subscribe((opened): void => {
        if (opened) {
          // focus the search field when opening
          this._focus();
          this.loadOrganizations('');
          this.initialData();
          if (this.singleSelect.value !== '' && this.singleSelect.value !== null) {
            this._dropDownDynamicService.restartPagination();
            this.loadOrganizations('');
            this.initialData();
          }
        } else {
          // clear it when closing
          this._dropDownDynamicService.restartPagination();
          // this.loadOrganizations('');
          this.initialData();
          this.writeValue(this.singleSelect.value);
        }
      });
    this._checkRef.markForCheck();
  }

  ngOnChanges(changes: SimpleChanges): void {
    //Called before any other lifecycle hook. Use it to inject dependencies, but avoid any serious work here.
    //Add '${implements OnChanges}' to the class.
    if (changes.currentValue) {
      return;
    } else {
      this.initialData();
      this._dropDownDynamicService.pageChanged$
        .pipe(takeUntil(this.destroyedSubject ? this.destroyedSubject : this._unsubscribeAll))
        .subscribe((pageInfo: PageInfo) => {
          this.pageInfo = pageInfo;
        });
    }
  }

  itemsPerPage(pageEvent: PageEvent): void {
    this.handlePageInfoAndEmit(pageEvent);
  }

  handlePageInfoAndEmit(pageEvent: PageEvent): void {
    const pageInfo: PageInfo = {
      totalElements: this.pageInfo.totalElements,
      currentPage: pageEvent.pageIndex,
      pageSize: pageEvent.pageSize,
      lastPage: this.pageInfo.lastPage,
      orderBy: this.pageInfo.orderBy,
    };
    this._dropDownDynamicService.onPageCursorChanged(pageInfo);

    // After emitting the new page info, reload organizations for the current
    // search query so the options shown reflect the selected page.
    // `initialData()` only wires the search input subscription and does not
    // trigger a load here, so calling `loadOrganizations` is necessary.
    this.loadOrganizations(this.searchForm.get('searchBy')?.value ?? '');
  }

  resetList(): void {
    this.searchForm.reset();
    this.searchForm.controls['searchBy'].setValue('');
    this.pageIndex = 0; // Reset page index
    this._dropDownDynamicService.restartPagination();
    this.initialData();
  }

  // Method to handle sorting
  sortData(sort: Sort): void {
    // Update PageInfo object for sorting
    const pageInfo: PageInfo = {
      totalElements: this.pageInfo.totalElements,
      currentPage: 0,
      pageSize: this.pageInfo.pageSize,
      orderBy: this.handleOrderBy(sort, 'email'),
      lastPage: this.pageInfo.lastPage,
    };
    this._dropDownDynamicService.onPageCursorChanged(pageInfo); // Update PageInfo in service

    // Verify if search gives no results to avoid continue and get an error
    if (this._dropDownDynamicService.data()?.organizations.length === 0) {
      return;
    }

    // Reload courses with search query after sorting
    this.loadOrganizations(this.searchForm.controls['searchBy'].value);
  }

  // Method to handle sorting and construct orderBy object
  handleOrderBy(sort: Sort, mainSortHeader: string): object {
    // Determine the column to sort by
    const column = sort.active === `${mainSortHeader}` ? `${mainSortHeader}` : sort.active;
    // Determine the sorting direction and convert it to uppercase
    const direction = sort.direction.toUpperCase();
    // Construct the orderBy object based on the column and direction
    const orderBy: Record<string, string | null> = {};
    orderBy[column] = direction === '' ? null : direction; // If direction is empty, set orderBy[column] to null
    return orderBy; // Return the constructed orderBy object
  }

  clearSearchAndFilter(): void {
    this.searchForm.reset();
    this.searchForm.controls['searchBy'].setValue('');
    this.initialData();
  }

  stopInTemplatePropagationAndDefault($event: Event): void {
    this.stopPropagationAndDefault($event);
  }

  initialData(): void {
    if (this._searchSubInitialized) {
      return;
    }
    this._searchSubInitialized = true;

    this.searchForm.controls['searchBy'].valueChanges
      .pipe(takeUntil(this._unsubscribeAll), debounceTime(300), distinctUntilChanged())
      .subscribe((searchText: string) => {
        this.pageIndex = 0;
        this.loadOrganizations(searchText);
      });
  }

  // ControlValueAccessor Implementation
  onChange: OrganizationSelectorChangeFn = () => { };
  onTouched: () => void = () => { };

  registerOnChange(fn: OrganizationSelectorChangeFn): void {
    this.selectListControl.valueChanges
      .pipe(takeUntil(this.destroyedSubject ? this.destroyedSubject : this._unsubscribeAll))
      .subscribe((value) => {
        // Preserve the selected organization only when the value is an object (Organization)
        if (value && typeof value === 'object') {
          this._previouslySelectedOrganization = value;
        }
        fn(value);
      });
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  writeValue(value = Object.assign({})): void {
    if (typeof value === 'object') {
      // Assuming that the ID is stored in a property named 'id' within the object
      if (value && value.id) {
        // Convert the object's ID into a UUID
        value = value.id;
      } else {
        // If the object doesn't have a valid ID, set the value to null
        value = null;
      }
    }
    if (value) {
      if (this.checkIfValidUUID(value)) {
        this._dropDownOrgByIdDynamicService
          .getOrganizationById(value)
          .subscribe((response): void => {
            const org = response.data.organizations[0];
            this._previouslySelectedOrganization = org;
            this.dataItemList = [org];
            this.selectListControl.setValue(org, {
              emitEvent: true,
            });
          });
      } else {
        // value may be an Organization object; preserve only if it's an object
        if (typeof value === 'object') {
          this._previouslySelectedOrganization = value as Organization;
        }
        this.selectListControl.setValue(value as Organization, { emitEvent: true });
        this.dataItemList = [value as Organization];
      }
    } else {
      this._previouslySelectedOrganization = null;
      this.selectListControl.reset(null);
    }
  }

  setDisabledState(isDisabled: boolean): void {
    this.isDisabled = isDisabled;
  }

  /***
   * Focuses the search input field
   * @private
   */
  _focus(): void {
    if (!this.searchSelectInput) {
      return;
    }
    // save and restore scrollTop of panel, since it will be reset by focus()
    // note: this is hacky
    const panel = this.singleSelect.panel?.nativeElement;
    const scrollTop = panel?.scrollTop;

    // focus
    this.searchSelectInput.nativeElement.focus();

    if (panel) {
      panel.scrollTop = scrollTop;
    }
  }

  /***
   * Resets the current search value
   * @param {boolean} focus whether to focus after resetting
   * @private
   */
  _reset(focus?: boolean): void {
    if (!this.searchSelectInput) {
      return;
    }
    this.searchSelectInput.nativeElement.value = '';
    this.onChange = (() => { }) as (val: Organization | string | null) => void;
    if (focus) {
      this._focus();
    }
  }

  /***
   * Handles the key down event with MatSelect.
   * Allows e.g. selecting with enter key, navigation with arrow keys, etc.
   * @param {KeyboardEvent} event
   * @private
   */
  _handleKeydown(event: KeyboardEvent): void {
    if (event.code === 'Space') {
      // do not propagate spaces to MatSelect, as this would select the currently active option
      event.stopPropagation();
    }
  }

  stopPropagationAndDefault = (event: Event): void => {
    event.stopImmediatePropagation();
    event.stopPropagation();
    event.preventDefault();
  };

  checkIfValidUUID = (str: string): boolean => {
    // Regular expression to check if string is a valid UUID
    const regexExp =
      /^[0-9a-fA-F]{8}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{12}$/gi;

    return regexExp.test(str);
  };

  /**
   * Track by function for ngFor loops
   *
   * @param index
   * @param item
   */
  trackByFn(_index: number, item = Object.assign({})): string {
    return item?.name;
  }

  loadOrganizations(searchQuery?: string): void {
    const orderBy = this.pageInfo.orderBy;
    const limit = this.pageInfo.pageSize;
    const page = this.pageInfo.currentPage + 1;

    let serviceCall$: ResultQuery<OrganizationResponse>;
    let serviceRef;

    // sysadmin and Elecate-family Global Admins can view every organization
    if (this.userIsSysAdmin || this.globalAdminBelongsToElecateFamily()) {
      serviceRef = this._dropDownDynamicService;
      serviceCall$ = serviceRef.getOrganizationForDropdown({
        query: searchQuery,
        limit,
        page,
        order: orderBy,
      });
    }
    // Other Global Admins: own organization plus Elecate
    else if (this.userIsGlobalAdmin) {
      const userOrgName = this.organizations?.users[0]?.organization?.name;
      const allowedNames = [userOrgName, 'Elecate'].filter((name): name is string => !!name);

      serviceRef = this._organizationByExactNameService;
      serviceCall$ = serviceRef.getOrganizationsByNames({
        names: allowedNames,
        order: orderBy,
      });
    }
    // org admin or student: only own org + Elecate
    else if (this.userIsOrgAdminOrStudent) {
      const userOrgName = this.organizations?.users[0]?.organization?.name;
      const allowedNames = [userOrgName, 'Elecate'].filter((n): n is string => !!n);

      serviceRef = this._organizationByExactNameService;
      serviceCall$ = serviceRef.getOrganizationsByNames({
        names: allowedNames,
        order: orderBy,
      });
    } else {
      // fallback (should not normally happen)
      serviceRef = this._dropDownDynamicService;
      serviceCall$ = serviceRef.getOrganizationForDropdown({
        query: searchQuery,
        limit,
        page,
        order: orderBy,
      });
    }

    serviceCall$.subscribe((result): void => {
      const count = result?.data.pageInfo.pages.count ?? result?.data.organizations.length ?? 0;

      // Users with unrestricted visibility receive a page from the API and need its metadata.
      if (this.canPaginateOrganizations()) {
        const pagedService = serviceRef as OrganizationSelectorService;
        const pageInfo: PageInfo = {
          totalElements: count,
          currentPage: pagedService.pageInfo.currentPage,
          pageSize: limit,
          orderBy,
          lastPage: Math.ceil(count / limit) - 1,
        };
        pagedService.onPageCursorChanged(pageInfo);
        pagedService._totalResults.next(count);
      }

      const orgs = result.data.organizations ?? [];
      const selected = this.selectListControl.value as Organization | null;
      const selectedId = selected?.id;
      // currentItem: the selected item (if it exists)
      let currentItem: Organization | null;
      if (selectedId) {
        currentItem = selected;
      } else {
        currentItem = null;
      }
      // availableItems: the current page, but excluding the selected item if it's within it
      let availableItems: Organization[];
      if (selectedId) {
        availableItems = orgs.filter((o) => o.id !== selectedId);
      } else {
        availableItems = orgs;
      }
      // final list for your normal *ngFor loop if you continue using it elsewhere
      this.dataItemList = availableItems;
      // saves current in a property for the template
      this.currentItem = currentItem;

      this._checkRef.markForCheck();
    });
  }

  // Method to handle page change event
  onPageChange(event: PageEvent): void {
    // Update PageInfo object with new page information
    const pageInfo: PageInfo = {
      totalElements: this.pageInfo.totalElements,
      currentPage: event.pageIndex,
      pageSize: event.pageSize,
      orderBy: this.pageInfo.orderBy,
      lastPage: this.pageInfo.lastPage,
    };
    this._dropDownDynamicService.onPageCursorChanged(pageInfo); // Update PageInfo in service
    this.loadOrganizations(this.searchForm.controls['searchBy'].value); // Load courses with search query
  }

  ngOnDestroy(): void {
    this._unsubscribeAll.next();
    this._unsubscribeAll.complete();
    this._unsubscribeAll.unsubscribe();
    this.resetList();
  }

  /**
   * A true super‑admin (sysadmin) has unrestricted access to every
   * organization.  A **global admin** is a lighter-weight role that
   * should *not* be treated the same way; global admins are only
   * allowed to see Elecate and the org where they were created.
   */
  get userIsSysAdmin(): boolean {
    return this.roles.includes('sysadmin');
  }

  /**
   * Global administrators have their own set of restrictions.  This
   * getter is used to branch logic where we previously lumped them
   * together with sysadmins.
   */
  get userIsGlobalAdmin(): boolean {
    return this.roles.includes('globaladmin');
  }

  private globalAdminBelongsToElecateFamily(): boolean {
    const userOrgName = this.organizations?.users[0]?.organization?.name?.trim();
    return this.userIsGlobalAdmin && ['Elecate', 'ElecateDemo'].includes(userOrgName ?? '');
  }

  private canPaginateOrganizations(): boolean {
    return this.userIsSysAdmin || this.globalAdminBelongsToElecateFamily();
  }

  get userIsOrgAdminOrStudent(): boolean {
    return this.roles.includes('orgadmin') || this.roles.includes('student');
  }

  shouldShowOrganizationPaginator(): boolean {
    return !this.userIsOrgAdminOrStudent && (this.pageInfo?.totalElements ?? 0) > this.pageSize;
  }

  getUserInformation(): void {
    this.loadUserProfileObservable$.pipe().subscribe({
      next: (user: KeycloakProfile) => {
        this._userService
          .getUserInformationByHasura(user.id)
          .pipe(takeUntil(this._unsubscribeAll))
          .subscribe((userData) => {
            this.organizations = userData.data;

            const userOrgName = this.organizations?.users[0]?.organization?.name;

            if (this.userIsSysAdmin || this.globalAdminBelongsToElecateFamily()) {
              this._dropDownDynamicService
                .getOrganizationForDropdown({ query: '', limit: 5, page: 1, order: { name: 'ASC' } })
                .subscribe((res) => {
                  const orgs = res.data.organizations;
                  this.dataItemList = orgs;
                  this.dataSource = new MatTableDataSource<Organization>(orgs);
                  const selected = orgs.find((org) => org.name === userOrgName) ?? orgs[0] ?? null;
                  this.writeValue(selected);
                  this._checkRef.markForCheck();
                });
              return;
            }

            const allowedNames = [userOrgName, 'Elecate'].filter(
              (name): name is string => !!name
            );

            this._organizationByExactNameService
              .getOrganizationsByNames({ names: allowedNames })
              .subscribe((res) => {
                const orgs = res.data.organizations;
                this.dataItemList = orgs;
                this.dataSource = new MatTableDataSource<Organization>(orgs);
                const selected = orgs.find((org) => org.name === userOrgName);
                this.writeValue(selected);
                this._checkRef.markForCheck();
              });
          });
      },
      error: (error) => {
        throw new Error(error);
      },
    });
  }

  compareOrganizations = (o1: Organization, o2: Organization): boolean => o1?.id === o2?.id;
}
